/**
 * Recovery for stale assets after a deploy.
 *
 * Route components are lazy-loaded with hashed filenames. When a tab has been open across a
 * deploy, the hashes it knows about are gone, so fetching a chunk fails. Because the server
 * falls back to index.html for unknown paths, the browser gets HTML where it expected a
 * module — which is why these surface under several different messages.
 *
 * The fix in every case is the same: reload once to pick up the current index.html.
 */

const reloadRecordKey = 'rs-chunk-reload-target';

// A reload can only fix this once; if the error repeats straight away, reloading again would
// spin. Recovery is still available for a later stale chunk once the window has passed.
const reloadCooldownMs = 30_000;

/**
 * Messages worth reloading for. Deliberately broad — a needless reload costs a moment, while a
 * missed one leaves the user on a dead page.
 */
const reloadMessages = [
	'Failed to fetch dynamically imported module',
	'Importing a module script failed',
	'Unable to preload CSS for',
	'is not a valid JavaScript MIME type',
	'error loading dynamically imported module',
	'Loading chunk',
	'ChunkLoadError',
	'Load failed',
];

/**
 * Messages that can ONLY mean a stale asset, so Sentry can drop them once we have recovered.
 * Narrower than the list above on purpose: 'Load failed' and 'Loading chunk' are generic
 * network failures that we still want reported.
 */
const unambiguousMessages = [
	'Failed to fetch dynamically imported module',
	'Importing a module script failed',
	'Unable to preload CSS for',
	'is not a valid JavaScript MIME type',
	'error loading dynamically imported module',
];

const serialize = (value: unknown): string => {
	if (!value) {
		return '';
	}

	if (typeof value === 'string') {
		return value;
	}

	if (value instanceof Error) {
		return `${value.name} ${value.message} ${value.stack ?? ''}`;
	}

	if (typeof value === 'object' && 'message' in value && typeof value.message === 'string') {
		return value.message;
	}

	try {
		return JSON.stringify(value);
	} catch {
		return '';
	}
};

const matches = (value: unknown, messages: string[]): boolean => {
	const serialized = serialize(value);

	if (!serialized) {
		return false;
	}

	return messages.some((message) => serialized.includes(message));
};

/** Should this error trigger a one-off reload? */
export const isStaleAssetError = (value: unknown): boolean => matches(value, reloadMessages);

/** Is this error definitely a stale asset, and therefore not worth reporting once recovered? */
export const isStaleAssetNoise = (value: unknown): boolean => matches(value, unambiguousMessages);

/**
 * Reload once to pick up current assets. Returns whether a reload was actually started, so
 * callers can tell a recovery from a no-op.
 */
export const reloadForStaleAsset = (target?: string): boolean => {
	if (typeof window === 'undefined') {
		return false;
	}

	const destination = target || window.location.pathname + window.location.search + window.location.hash;

	let lastAttemptedAt = 0;

	try {
		const stored = window.sessionStorage.getItem(reloadRecordKey);

		if (stored) {
			const parsed: unknown = JSON.parse(stored);

			if (typeof parsed === 'object' && parsed !== null && 'at' in parsed && typeof parsed.at === 'number') {
				lastAttemptedAt = parsed.at;
			}
		}
	} catch {
		// A private window (or cleared storage) can throw here. Losing the guard only means we
		// might reload once more than necessary, so carry on.
	}

	if (Date.now() - lastAttemptedAt < reloadCooldownMs) {
		return false;
	}

	try {
		window.sessionStorage.setItem(reloadRecordKey, JSON.stringify({ target: destination, at: Date.now() }));
	} catch {
		// As above — proceed without the guard rather than leaving the user stuck.
	}

	window.location.assign(destination);

	return true;
};

/**
 * Vite raises `vite:preloadError` when it cannot preload a chunk. That never reaches the
 * router, so it needs its own listener — this is the gap that let the CSS preload failures
 * keep reporting.
 */
export const registerStaleAssetRecovery = (): void => {
	if (typeof window === 'undefined') {
		return;
	}

	window.addEventListener('vite:preloadError', (event) => {
		// Stop Vite rethrowing; we are handling it by reloading.
		event.preventDefault();
		reloadForStaleAsset();
	});
};
